import React, { FunctionComponent } from 'react';
import { useLocation } from 'react-router';

import { Header as CommonHeader } from 'scenes/common/components/Header';
import {
  isBusinessError,
  isIneligiblePromoCodeError,
  isLoadingWithNoData,
  isPromoCodeError,
  isQuoteSaved,
} from 'scenes/common/helpers/apiData';
import { isUnderMaintenance } from 'scenes/common/selectors/configSelectors';
import { FunnelType, LocationState } from 'scenes/common/types';
import {
  hasPreviousButton,
  showHeaderMarketingCampaignInformation,
  showHeaderQuoteInformation,
  showPaymentConfirmationHeader,
} from 'scenes/landlord/components/helpers';
import {
  getCampaign,
  getQuoteNumber,
  getSelectedInsuranceOption,
  getSelectedPaymentFrequency,
  getFirstInstalment,
  getMonthlyInstalment,
} from 'scenes/landlord/selectors';
import { LandlordFlowType, LandlordFullPageProps } from 'scenes/landlord/types';

import { HomeForRent } from './HomeForRent';

export const Header: FunctionComponent<Pick<
  LandlordFullPageProps,
  'funnelState' | 'onPrevious'
>> = ({ funnelState, onPrevious }) => {
  const location = useLocation<LocationState<LandlordFlowType>>();
  const { coverType, premium } = getSelectedInsuranceOption(funnelState);

  const hasBusinessErrors =
    isBusinessError(funnelState.apiResponses.quoteDetails) &&
    !isPromoCodeError(funnelState.apiResponses.quoteDetails) &&
    !isIneligiblePromoCodeError(funnelState.apiResponses.quoteDetails);

  const isApiQuoteSaved = isQuoteSaved(funnelState.apiResponses.quoteDetails);

  const isApiLoadingWithNoData = isLoadingWithNoData(
    funnelState.apiResponses.quoteDetails
  );

  const underMaintenance = isUnderMaintenance(
    funnelState.apiResponses.config,
    FunnelType.Landlord
  );

  return (
    <CommonHeader
      onPrevious={onPrevious}
      quoteNumber={getQuoteNumber(funnelState)}
      coverType={coverType}
      premium={premium}
      campaign={getCampaign(funnelState)}
      selectedPaymentFrequency={getSelectedPaymentFrequency(funnelState)}
      ImageYouAreCovered={() => <HomeForRent />}
      firstInstalment={getFirstInstalment(funnelState)}
      followingMonths={getMonthlyInstalment(funnelState)}
      showPaymentConfirmationHeader={showPaymentConfirmationHeader(
        funnelState.flow,
        funnelState.step
      )}
      showMarketingCampaign={
        !hasBusinessErrors &&
        !isApiQuoteSaved &&
        !isApiLoadingWithNoData &&
        !underMaintenance &&
        showHeaderMarketingCampaignInformation(funnelState.flow)
      }
      hasQuoteInformation={
        !hasBusinessErrors &&
        !isApiQuoteSaved &&
        !isApiLoadingWithNoData &&
        !underMaintenance &&
        showHeaderQuoteInformation(funnelState.flow, funnelState.step)
      }
      hasPreviousButton={
        !hasBusinessErrors &&
        !isApiQuoteSaved &&
        !isApiLoadingWithNoData &&
        !underMaintenance &&
        hasPreviousButton(location)
      }
    />
  );
};
